import type { ComponentType } from 'react'
import { isMultiSelectSurveyQuestion } from '../../../lib/choice-answer'
import type { ChoiceDraftAnswer, SurveyDraftAnswer, SurveyQuestion } from '../../../lib/survey-question.types'
import { DateQuestion } from './DateQuestion'
import { DropdownQuestion } from './DropdownQuestion'
import { LikertQuestion } from './LikertQuestion'
import { LongTextQuestion } from './LongTextQuestion'
import { MultiSelectQuestion } from './MultiSelectQuestion'
import { PictureChoiceQuestion } from './PictureChoiceQuestion'
import { RankingQuestion } from './RankingQuestion'
import { RatingQuestion } from './RatingQuestion'
import { ShortTextQuestion } from './ShortTextQuestion'
import { VideoQuestion } from './VideoQuestion'
import { SingleSelectQuestion } from './SingleSelectQuestion'
import { SliderQuestion } from './SliderQuestion'
import { YesNoQuestion } from './YesNoQuestion'

export interface SurveyQuestionRendererProps {
  readonly question: SurveyQuestion
  readonly value: SurveyDraftAnswer
  readonly onChange: (value: SurveyDraftAnswer) => void
}

export interface SurveyChoiceQuestionRendererProps extends SurveyQuestionRendererProps {
  readonly displaySeed: string
}

const CHOICE_SURVEY_QUESTION_TYPES = ['multiple_choice', 'checkbox', 'dropdown'] as const

export type SurveyChoiceQuestionType = (typeof CHOICE_SURVEY_QUESTION_TYPES)[number]

export function isSurveyChoiceQuestionType(
  type: SurveyQuestion['type'],
): type is SurveyChoiceQuestionType {
  return (CHOICE_SURVEY_QUESTION_TYPES as readonly string[]).includes(type)
}

export type SurveyQuestionRendererComponent =
  | ComponentType<SurveyQuestionRendererProps>
  | ComponentType<SurveyChoiceQuestionRendererProps>

function coerceString(value: SurveyDraftAnswer): string | null {
  return typeof value === 'string' ? value : null
}

function coerceNumber(value: SurveyDraftAnswer): number | null {
  return typeof value === 'number' ? value : null
}

function wrapStringQuestion(
  Component: ComponentType<{
    readonly question: SurveyQuestion
    readonly value: string | null
    readonly onChange: (value: string) => void
  }>,
) {
  return function StringQuestion({ question, value, onChange }: SurveyQuestionRendererProps) {
    return (
      <Component
        question={question}
        value={coerceString(value)}
        onChange={(next) => onChange(next)}
      />
    )
  }
}

function wrapNumberQuestion(
  Component: ComponentType<{
    readonly question: SurveyQuestion
    readonly value: number | null
    readonly onChange: (value: number) => void
  }>,
) {
  return function NumberQuestion({ question, value, onChange }: SurveyQuestionRendererProps) {
    return (
      <Component
        question={question}
        value={coerceNumber(value)}
        onChange={(next) => onChange(next)}
      />
    )
  }
}

function YesNoWrapper({ question, value, onChange }: SurveyQuestionRendererProps) {
  const v = coerceString(value)
  return (
    <YesNoQuestion
      question={question}
      value={v}
      onChange={(next) => onChange(next)}
    />
  )
}

function ChoiceWrapper({
  question,
  value,
  displaySeed,
  onChange,
  Component,
}: SurveyChoiceQuestionRendererProps & {
  readonly Component: ComponentType<{
    readonly question: SurveyQuestion
    readonly value: SurveyDraftAnswer
    readonly displaySeed: string
    readonly onChange: (value: ChoiceDraftAnswer) => void
  }>
}) {
  return (
    <Component
      question={question}
      value={value}
      displaySeed={displaySeed}
      onChange={(next) => onChange(next)}
    />
  )
}

function RankingQuestionWrapper({ question, value, onChange }: SurveyQuestionRendererProps) {
  return (
    <RankingQuestion
      question={question}
      value={Array.isArray(value) ? value : null}
      onChange={(next) => onChange(next)}
    />
  )
}

export const surveyQuestionRendererByType: Partial<
  Record<SurveyQuestion['type'], SurveyQuestionRendererComponent>
> = {
  yes_no: YesNoWrapper,
  multiple_choice: (props: SurveyChoiceQuestionRendererProps) =>
    ChoiceWrapper({
      ...props,
      Component: isMultiSelectSurveyQuestion(props.question)
        ? MultiSelectQuestion
        : SingleSelectQuestion,
    }),
  checkbox: (props: SurveyChoiceQuestionRendererProps) =>
    ChoiceWrapper({ ...props, Component: MultiSelectQuestion }),
  dropdown: (props: SurveyChoiceQuestionRendererProps) =>
    ChoiceWrapper({ ...props, Component: DropdownQuestion }),
  rating: wrapNumberQuestion(RatingQuestion),
  likert: wrapNumberQuestion(LikertQuestion),
  slider: wrapNumberQuestion(SliderQuestion),
  ranking: RankingQuestionWrapper,
  picture_choice: wrapStringQuestion(PictureChoiceQuestion),
  short_text: wrapStringQuestion(ShortTextQuestion),
  long_text: wrapStringQuestion(LongTextQuestion),
  date: wrapStringQuestion(DateQuestion),
  video: wrapStringQuestion(VideoQuestion),
}
